import { defineAsyncComponent } from 'vue';
import { contactTypeRoutes } from './contactRoutes';
import { getCrmTabs } from '@/modules/index';

// The generic contact routes, plus the payment-methods tab a customer alone has.
const customerRoutes = contactTypeRoutes('customer', 'customers', 'customers-list');
customerRoutes.children[0].children.push({
    path: 'payment-methods',
    name: 'customer-payment-methods',
    component: () => import('@modules/Payments/Resources/js/components/TabPaymentMethods.vue'),
    props: true
});

export default {
    slug: 'contacts',
    ability: 'contacts',
    components(app) {
        app.component(
            'app-addresses',
            defineAsyncComponent(() => import('./components/addresses/Addresses.vue'))
        );
        app.component(
            'app-contacts',
            defineAsyncComponent(() => import('./components/contacts/Contacts.vue'))
        );
        app.component(
            'app-contact-opportunities',
            defineAsyncComponent(() => import('./components/opportunities/ContactOpportunities.vue'))
        );
    },
    /**
     * The customer facet of a contact belongs here, not to Accounting.
     *
     * It is the same list, the same component and the same `contacts` ability,
     * filtered on who buys from us — so it used to hang off Accounting for the
     * one reason that the Sales menu did, and it left with it: an organization
     * that bought no part of Accounting lost its customer list, which it had
     * never been sold and could not be sold. Contacts is a core module, so from
     * here the list is simply always there.
     */
    contactTypes: [
        {
            slug: 'customer',
            label: () => i18n.t('Customers'),
            // One record of the section: « Créer comme : Client ».
            singularLabel: () => i18n.t('Customer'),
            listRoute: 'customers-list',
            previewRoute: 'customer-preview',
            routePrefix: 'customer'
        },
        {
            // A prospect is a contact one hopes to sell to. It used to be
            // declared by Opportunities, so an organization without the pipeline
            // lost the list of the people it was courting — which it had never
            // been sold and could not be.
            slug: 'prospect',
            label: () => i18n.t('Prospects'),
            singularLabel: () => i18n.t('Prospect'),
            listRoute: 'prospects-list',
            previewRoute: 'prospect-preview',
            routePrefix: 'prospect'
        }
    ],
    routes: [
        contactTypeRoutes('contact', 'contacts', 'contact-list'),
        customerRoutes,
        contactTypeRoutes('prospect', 'prospects', 'prospects-list')
    ],
    // The CRM settings screen hangs off Contacts, not Opportunities: every plan
    // includes contacts and prospects, so the door stays where the CRM is. Its
    // tabs are the modules' own (`crmTabs` / `crmRoutes`, see getCrmTabs()).
    settingsRoutes: [
        {
            path: 'crm',
            name: 'settings-crm',
            component: () => import('@modules/Contacts/Resources/js/pages/settings/CrmSettings.vue')
        }
    ],
    // The list of loss reasons is the CRM's, not the pipeline's: a prospect
    // gives one without any opportunity in sight, so the tab lives here and
    // shows on plans without Opportunities too.
    crmTabs: () => [
        {
            position: 15,
            title: i18n.t('Loss reasons'),
            icon: 'close-octagon-outline',
            route: 'settings-crm-loss-reasons',
            ability: 'contacts.manage'
        }
    ],
    crmRoutes: [
        {
            path: 'loss-reasons',
            name: 'settings-crm-loss-reasons',
            component: () => import('@modules/Contacts/Resources/js/pages/settings/LossReasonSettings.vue')
        }
    ],
    settings: () => [
        {
            title: i18n.t('Applications'),
            order: 10,
            items: [
                {
                    // Shown as soon as one of its tabs is readable, the way General is.
                    title: i18n.t('CRM'),
                    icon: 'account-group',
                    route: 'settings-crm',
                    position: 10,
                    visible: () => getCrmTabs().length > 0
                }
            ]
        }
    ],
    nav: () => [
        {
            section: 'crm',
            items: [
                {
                    title: i18n.t('Contacts'),
                    icon: 'book-open-variant',
                    to: { name: 'contact-list' },
                    position: 6
                },
                {
                    title: i18n.t('Prospects'),
                    icon: 'account-search',
                    to: { name: 'prospects-list' },
                    position: 1
                }
            ]
        },
        {
            // Under Sales, where people look for their customers, even though
            // nothing of Sales need be bought to hold them.
            section: 'sales',
            items: [
                {
                    title: i18n.t('Customers'),
                    icon: 'account-cash-outline',
                    to: { name: 'customers-list' },
                    position: 1
                }
            ]
        }
    ]
};
